import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra space to ensure CTA is scrollable + 20px buffer */
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.lg};
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px); /* Ensure content is tall enough to scroll */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 80px);
  }
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: 8px;
`;

const ContinueButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  /* Button is now in content flow, adjust spacing */
  margin-top: 0;
  margin-bottom: 24px;
  /* Better width handling for longer text */
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  /* Ensure proper touch targets on mobile */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }

  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    min-width: 150px;
  }
`;

const NoDataMessage = styled.div`
  background: ${theme.colors.neutral}20;
  border: 2px solid ${theme.colors.neutral}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin: ${theme.spacing.lg} 0;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const NoDataTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const NoDataText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
`;

// Win Rate Visualization Components (reused from MembershipSelectionScreen)
const WinRateContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: 0 auto 38px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WinRateTitle = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: 0;
  text-align: center;
`;

const WinRateChart = styled.div`
  width: 100%;
  max-width: 80vw;
  height: 200px;
  position: relative;
  background: ${theme.colors.surface};
  padding: 0 ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.md};
  box-sizing: border-box;
`;

const WinRateBar = styled.div`
  position: absolute;
  bottom: 0;
  width: ${props => props.width}%;
  height: ${props => props.height}%;
  background: ${props => {
    if (props.isTargetRange) return theme.colors.doryLighterBlue; // Light blue for target range
    return `${theme.colors.doryGreen}80`; // Light green for others
  }};
  border-radius: ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0 0;
  transition: all ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
  left: ${props => props.left}%;
  
  &:hover {
    transform: scaleY(1.05);
    transform-origin: bottom;
  }
`;

const WinRateLabel = styled.div`
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: 12px;
  color: ${theme.colors.textLight};
  font-weight: ${props => props.isBold ? theme.typography.weights.bold : theme.typography.weights.regular};
`;

const DateLabel = styled.div`
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: 11px;
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
  white-space: nowrap;
`;


const SVGLine = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;

const MembershipSummaryScreen = ({ data, onNext, userProgress = {} }) => {
  const [optimalPaceData, setOptimalPaceData] = useState(userProgress.calculatedMetrics?.optimalPace);
  const [membershipData, setMembershipData] = useState(userProgress.membershipSelection);
  
  // Update data when userProgress changes
  useEffect(() => {
    const newOptimalPaceData = userProgress.calculatedMetrics?.optimalPace;
    if (newOptimalPaceData) {
      setOptimalPaceData(newOptimalPaceData);
    }
    
    const newMembershipData = userProgress.membershipSelection;
    if (newMembershipData) {
      setMembershipData(newMembershipData);
    }
  }, [userProgress]);

  // Process merge fields and format text content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Helper function to render the win rate visualization
  const renderWinRateVisualization = () => {
    if (!optimalPaceData?.optimalHours || !membershipData) return null;

    const currentPace = Math.round(optimalPaceData.optimalHours);
    const targetPace = membershipData.targetOptimalPace || currentPace;
    
    // Show bars 13 to 18 when Optimal Pace is 18, otherwise show from current pace to target pace
    let startPace, endPace;
    if (currentPace === 18) {
      startPace = 13;
      endPace = 18;
    } else {
      startPace = currentPace;
      endPace = targetPace;
    }
    
    const totalBars = endPace - startPace + 1;
    const barWidth = 100 / totalBars;
    
    return (
      <WinRateContainer>
        <WinRateTitle>Your Optimal Pace Journey</WinRateTitle>
        <WinRateChart>
          {/* Render bars from current pace to target pace */}
          {Array.from({ length: totalBars }, (_, index) => {
            const pace = startPace + index;
            const winRate = Math.round((pace / 18) * 100);
            
            // Create more variance in bar heights - first bars smaller, later bars larger
            // Use a progressive scaling factor that makes early bars shorter and later bars taller
            const progressRatio = index / (totalBars - 1); // 0 to 1
            const varianceFactor = 0.3 + (0.7 * progressRatio); // 0.3 to 1.0
            const barHeight = winRate * varianceFactor;
            
            const barLeft = (index * barWidth);
            const isCurrentPace = pace === currentPace;
            const isTargetPace = pace === targetPace;
            
            // Show "Today" for first bar, then calculate dates for subsequent bars
            let dateString;
            if (index === 0) {
              dateString = "Today";
            } else {
              // Calculate date for this bar (today + 17 days * index)
              const today = new Date();
              const barDate = new Date(today);
              barDate.setDate(today.getDate() + (17 * index));
              
              // Format date as "Sept 22nd" with ordinal suffix
              const month = barDate.toLocaleDateString('en-US', { month: 'short' });
              const day = barDate.getDate();
              
              // Add ordinal suffix (st, nd, rd, th)
              let ordinalSuffix;
              if (day >= 11 && day <= 13) {
                ordinalSuffix = 'th';
              } else {
                switch (day % 10) {
                  case 1: ordinalSuffix = 'st'; break;
                  case 2: ordinalSuffix = 'nd'; break;
                  case 3: ordinalSuffix = 'rd'; break;
                  default: ordinalSuffix = 'th'; break;
                }
              }
              
              dateString = `${month} ${day}${ordinalSuffix}`;
            }
            
            return (
              <WinRateBar
                key={pace}
                width={barWidth - 1} // Small gap between bars
                height={barHeight}
                left={barLeft}
                isTargetRange={true} // All bars in this range are highlighted
              >
                {currentPace !== 18 && <DateLabel>{dateString}</DateLabel>}
                <WinRateLabel isBold={isCurrentPace || isTargetPace}>{pace}</WinRateLabel>
              </WinRateBar>
            );
          })}
          
          {/* Connecting lines between each bar */}
          {totalBars > 1 && currentPace !== 18 && (() => {
            const lines = [];
            
            for (let i = 0; i < totalBars - 1; i++) {
              const currentBarIndex = i;
              const nextBarIndex = i + 1;
              
              // Calculate horizontal centers
              const currentBarCenter = (currentBarIndex * barWidth) + ((barWidth - 1) / 2);
              const nextBarCenter = (nextBarIndex * barWidth) + ((barWidth - 1) / 2);
              
              // Calculate vertical positions (middle of each bar's height)
              const currentPaceValue = startPace + i;
              const nextPaceValue = startPace + i + 1;
              const currentWinRate = Math.round((currentPaceValue / 18) * 100);
              const nextWinRate = Math.round((nextPaceValue / 18) * 100);
              
              // Apply the same variance factor to line calculations
              const currentProgressRatio = i / (totalBars - 1);
              const nextProgressRatio = (i + 1) / (totalBars - 1);
              const currentVarianceFactor = 0.3 + (0.7 * currentProgressRatio);
              const nextVarianceFactor = 0.3 + (0.7 * nextProgressRatio);
              
              const currentBarHeight = currentWinRate * currentVarianceFactor;
              const nextBarHeight = nextWinRate * nextVarianceFactor;
              
              // Position line endpoints at the middle of each bar's height
              const currentBarMiddle = 100 - currentBarHeight + (currentBarHeight * 0.5);
              const nextBarMiddle = 100 - nextBarHeight + (nextBarHeight * 0.5);
              
              lines.push(
                <line
                  key={`line-${i}`}
                  x1={`${currentBarCenter}%`}
                  y1={`${currentBarMiddle}%`}
                  x2={`${nextBarCenter}%`}
                  y2={`${nextBarMiddle}%`}
                  stroke={theme.colors.doryDarkerBlue}
                  strokeWidth="4"
                />
              );
            }
            
            return (
              <SVGLine>
                {lines}
              </SVGLine>
            );
          })()}
        </WinRateChart>
      </WinRateContainer>
    );
  };

  // Check if we have the required data
  if (!optimalPaceData || !membershipData) {
    return (
      <Container>
        <Content>
          {htmlContent && (
            <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
          )}
          
          <NoDataMessage>
            <NoDataTitle>No Data Available</NoDataTitle>
            <NoDataText>
              Complete a journal entry and membership selection to see your summary.
            </NoDataText>
          </NoDataMessage>
          
          {/* CTA button appears right after content */}
          <ContinueButton onClick={() => onNext?.(data.key)}>
            {data.content?.ctaText || 'Continue'}
          </ContinueButton>
        </Content>
      </Container>
    );
  }

  const currentPace = Math.round(optimalPaceData.optimalHours);
  const targetPace = membershipData.targetOptimalPace || currentPace;
  const pointsToImprove = targetPace - currentPace;

  return (
    <Container>
      <Content>
        {htmlContent && (
          <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {/* Win Rate Visualization */}
        {renderWinRateVisualization()}
        
        {/* Summary Message */}
        <Description
          dangerouslySetInnerHTML={{
            __html: pointsToImprove > 0
              ? (() => {
                  // Calculate the target date (today + 17 days * (targetPace - currentPace))
                  const today = new Date();
                  const targetDate = new Date(today);
                  targetDate.setDate(today.getDate() + (17 * (targetPace - currentPace)));
                  
                  // Format target date as "January 2nd 2026"
                  const month = targetDate.toLocaleDateString('en-US', { month: 'long' });
                  const day = targetDate.getDate();
                  const year = targetDate.getFullYear();
                  
                  // Add ordinal suffix
                  let ordinalSuffix;
                  if (day >= 11 && day <= 13) {
                    ordinalSuffix = 'th';
                  } else {
                    switch (day % 10) {
                      case 1: ordinalSuffix = 'st'; break;
                      case 2: ordinalSuffix = 'nd'; break;
                      case 3: ordinalSuffix = 'rd'; break;
                      default: ordinalSuffix = 'th'; break;
                    }
                  }
                  
                  const targetDateString = `${month} ${day}${ordinalSuffix} ${year}`;
                  
                  return `You can expect to arrive at your new Optimal Pace of <strong>${targetPace}</strong> on <strong>${targetDateString}</strong>.`;
                })()
              : `You're investing <strong>$${membershipData.monthlyAmount}</strong> per month to maintain your current Optimal Pace of <strong>${currentPace}</strong> and <strong>${Math.round((currentPace / 18) * 100)}%</strong> performance ability.`
          }}
        />
        
        {/* CTA button appears right after content */}
        <ContinueButton onClick={() => onNext?.(data.key)}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default MembershipSummaryScreen;
